import { Box, Group, MantineBreakpoint, rem, TextProps } from "@mantine/core"
import { IconCopy } from "@tabler/icons-react"
import { forwardRef } from "react"

import { ExtendedCustomColors } from "mantine"

import { Label } from "../../atoms/Label/Label"
import { LabelWithTooltip } from "../LabelWithTooltip/LabelWithTooltip"

import { Missing } from "./Missing"

type BaseProps = {
  value: string | undefined
  hoverHelp?: string
  tooltip?: string
  c?: string | Partial<Record<MantineBreakpoint, ExtendedCustomColors>>
} & TextProps

type CopyableProps = BaseProps & {
  copyable: true
  onCopy: () => void
}

type NonCopyableProps = BaseProps & {
  copyable?: false
  onCopy?: never
}

type Props = CopyableProps | NonCopyableProps

export const TextCell = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { value, c, tooltip, hoverHelp, copyable, onCopy, ...restProps } = props

  if (!value) return <Missing />

  const handleCopy = () => {
    if (copyable && onCopy) {
      navigator.clipboard.writeText(value)
      onCopy()
    }
  }

  if (hoverHelp && !copyable) {
    return (
      <LabelWithTooltip
        label={value}
        hoverHelperText={hoverHelp}
        {...restProps}
      />
    )
  }

  if (copyable) {
    return (
      <Group wrap="nowrap" align="baseline" gap={1}>
        <Box w={12} h={12}>
          <IconCopy
            style={{ width: rem(12), height: rem(12), cursor: "pointer" }}
            stroke={1.5}
            onClick={handleCopy}
            aria-label="Copy to clipboard"
          />
        </Box>
        <Label
          label={value}
          onClick={handleCopy}
          c={c}
          tooltip={tooltip}
          {...restProps}
        />
      </Group>
    )
  }

  return (
    <Label
      ref={ref}
      label={value}
      c={c}
      fz="var(--table-cell-text-size)"
      tooltip={tooltip}
      {...restProps}
    />
  )
})
